import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack, useMantineTheme } from '@mantine/core';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { defaultPersonAttributes, type IImportColumn } from '@feathr/blackbox';
import {
  Button,
  CardContent,
  CardHeader,
  CardV2,
  Fieldset,
  Form,
  FormSummary,
  FormSummaryItem,
  Input,
  Pill,
  Skeleton,
  TanStackTable,
  Text,
} from '@feathr/components';
import { useFlags, useStore } from '@feathr/extender/state';
import { isImportsRevampEnabled } from '@feathr/extender/utils/importsRevampFlags';
import type { IValidation } from '@feathr/rachis';

import type { IImportButtonProps } from './DataImportPage';
import { isImporterEditable } from './DataImportPage.utils';
import type { IMappingSummaryRow } from './DataImportStepThree.utils';
import {
  countMappedColumns,
  getMappingSummaryRows,
  isFirstRowHeader,
} from './DataImportStepThree.utils';

import * as styles from './DataImportPage.css';

interface IProps {
  disabled?: boolean;
  importer: Importer;
  onPrev: () => void;
  ImportButton: React.FunctionComponent<IImportButtonProps>;
  /** False when the page renders step navigation in its own action bar. */
  showActions?: boolean;
}

interface ISummaryProps {
  importer: Importer;
}

interface IColumnRowProps {
  columnName: string;
  fieldName: string;
}

export const validateStepThree = (importer: Importer): IValidation<string[]> => {
  return importer.validate(['name', 'file_name', 'tag_ids', 'column_mappers'], false);
};

const ImportDetailSummary = observer(({ importer }: ISummaryProps) => {
  const { t } = useTranslation();

  return (
    <>
      <FormSummaryItem label={t('Import Name')} value={importer.get('name')} />
      <FormSummaryItem label={t('File Name')} value={importer.get('file_name')} />
    </>
  );
});

interface ITagsSummaryProps extends ISummaryProps {
  /** Renders the revamped review's copy: a translated label, and a dash for no tags. */
  isRevampEnabled?: boolean;
}

const TagsSummary = observer(({ importer, isRevampEnabled = false }: ITagsSummaryProps) => {
  const { t } = useTranslation();
  const { Tags } = useStore();

  if (importer.isOptOut) {
    return null;
  }

  const tags = Tags.list({
    filters: {
      is_archived__ne: true,
      collection: 'Partner',
    },
    pagination: { page_size: 1000 },
  });

  const selectedTags = tags.models.filter((tag) => importer.get('tag_ids', []).includes(tag.id));
  const value = !tags.isPending ? (
    selectedTags.map((tag) => (
      <Pill key={tag.get('name')} theme={'neutral'}>
        {tag.get('name')}
      </Pill>
    ))
  ) : (
    <Skeleton />
  );

  return isRevampEnabled ? (
    <FormSummaryItem
      label={t('Tags')}
      // An empty item reads as still loading, so no tags gets the same dash as other blanks.
      value={!tags.isPending && !selectedTags.length ? '—' : value}
    />
  ) : (
    <FormSummaryItem label={'Tags'} value={value} />
  );
});

const RevampDetailSummary = observer(({ importer }: ISummaryProps) => {
  const { t } = useTranslation();
  const columnNames = importer.get('column_names', []);
  const isHeader = isFirstRowHeader(columnNames);
  const { mapped, total } = countMappedColumns(columnNames, importer.get('column_mappers'));

  let headerValue = '—';
  if (isHeader !== undefined) {
    headerValue = isHeader ? t('Yes') : t('No');
  }

  return (
    <CardV2 name={'review-details'} width={'full'}>
      <CardHeader title={t('Check the details before importing')} />
      <CardContent>
        <FormSummary>
          <FormSummaryItem label={t('Import name')} value={importer.get('name') || '—'} />
          <FormSummaryItem
            label={t('File')}
            name={'review-file'}
            value={importer.get('file_name') || '—'}
          />
          <FormSummaryItem
            label={t('First row is a header')}
            name={'review-header-row'}
            value={headerValue}
          />
          <FormSummaryItem
            label={t('Columns mapped')}
            name={'review-columns-mapped'}
            value={t('{{mapped}} of {{total}}', { mapped, total })}
          />
          <TagsSummary importer={importer} isRevampEnabled={true} />
        </FormSummary>
      </CardContent>
    </CardV2>
  );
});

function mappingSummaryColumns(t: TFunction): Array<ColumnDef<IMappingSummaryRow>> {
  return [
    {
      id: 'column',
      header: t('Column in your file'),
      meta: { isFlex: true, isPrimary: true },
      cell: ({ row }: CellContext<IMappingSummaryRow, unknown>): string => row.original.column,
    },
    {
      id: 'field',
      header: t('Feathr field'),
      meta: { isFlex: true },
      cell: ({ row }: CellContext<IMappingSummaryRow, unknown>): JSX.Element => {
        const { field } = row.original;
        if ('name' in field) {
          return <Text.DefaultRegular>{field.name}</Text.DefaultRegular>;
        }
        return (
          <Text.DefaultRegular className={styles.notImported}>
            {field.status === 'removed' ? t('Not imported') : t('Not mapped')}
          </Text.DefaultRegular>
        );
      },
    },
  ];
}

/** Each mapping row as file column → Feathr field, then the file's columns left out of the import. */
const ColumnMappingSummary = observer(({ importer }: ISummaryProps) => {
  const { t } = useTranslation();
  const { CustomFields } = useStore();
  const fieldNames = Object.fromEntries(
    [
      ...defaultPersonAttributes,
      ...importer.getUsedCustomFields(CustomFields).map((f) => f.toJS()),
    ].map((field) => [field.id, field.u_key]),
  );
  const rows = getMappingSummaryRows(
    importer.get('column_names', []),
    importer.get('column_mappers'),
    fieldNames,
  );

  return (
    <CardV2 name={'review-columns'} width={'full'}>
      <CardHeader title={t('Column mapping')} />
      <CardContent>
        <TanStackTable<IMappingSummaryRow>
          columns={mappingSummaryColumns(t)}
          idKey={'id'}
          isPaginated={false}
          items={rows}
          noDataText={t('No columns to import.')}
        />
      </CardContent>
    </CardV2>
  );
});

function ColumnRow({ columnName, fieldName }: Readonly<IColumnRowProps>): JSX.Element {
  return (
    <Fieldset direction={'column'}>
      <Input disabled={true} type={'text'} value={columnName} />
      <FontAwesomeIcon className={styles.arrow} icon={faArrowRight} />
      <Input disabled={true} type={'text'} value={fieldName} />
    </Fieldset>
  );
}

const ColumnsSummary = observer(({ importer }: ISummaryProps) => {
  const { CustomFields } = useStore();
  const fields = [
    ...defaultPersonAttributes,
    ...importer.getUsedCustomFields(CustomFields).map((f) => f.toJS()),
  ];
  const columns = importer
    .get('column_mappers')
    .filter((column) => column.column_name !== '') as IObservableArray<IImportColumn>;

  return (
    <FormSummaryItem
      label={'Columns'}
      value={
        <>
          {columns.map((column) => (
            <ColumnRow
              columnName={column.column_name}
              fieldName={fields.find((f) => f.id === column.feathr_attr)?.u_key ?? ''}
              key={`${column.column_name}-${column.feathr_attr ?? ''}`}
            />
          ))}
        </>
      }
    />
  );
});

function DataImportStepThree({
  disabled = false,
  importer,
  ImportButton,
  onPrev,
  showActions = true,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const isRevampEnabled = isImportsRevampEnabled(useFlags());
  const isEditable = isImporterEditable(importer.get('state'));
  const theme = useMantineTheme();

  const actions = showActions
    ? [
        <Button key={'prev'} onClick={onPrev}>
          {t('Previous')}
        </Button>,
        <ImportButton
          disabled={disabled || !isEditable}
          importer={importer}
          key={'importButton'}
        />,
      ]
    : undefined;

  const legacyDescription = importer.isOptOut
    ? t(
        'In order for your data to be properly imported into Feathr, each column of your spreadsheet must be mapped to a field on each person that is being imported. You can map each column to a field that already exists or to a new custom field you create.',
      )
    : t(
        'Review your settings and make sure you are tagging and associating your imported partners how you want.',
      );

  return (
    <Form
      actions={actions}
      // The revamped step's first card header carries the description.
      description={isRevampEnabled ? undefined : legacyDescription}
      label={t('Review')}
      width={isRevampEnabled ? 'wide' : 'narrow'}
    >
      {isRevampEnabled ? (
        <Stack gap={theme.spacing['4']}>
          <RevampDetailSummary importer={importer} />
          <ColumnMappingSummary importer={importer} />
        </Stack>
      ) : (
        <FormSummary>
          <ImportDetailSummary importer={importer} />
          <TagsSummary importer={importer} />
          <ColumnsSummary importer={importer} />
        </FormSummary>
      )}
    </Form>
  );
}

export default observer(DataImportStepThree);
